import { tss } from 'tss-react/mui';
import './circlechart.css';

const useStyles = tss
    .withNestedSelectors<
        'circle_chart__percent_number' | 'circle_chart__percent'
    >()
    .create(({ theme, classes }) => ({
        root: {
            display: 'inline-block',
            verticalAlign: 'middle',
        },
        large: {
            width: '60px',
            [theme.breakpoints.only('xs')]: {
                width: 'auto',
            },
        },
        small: {
            width: '40px',
            [theme.breakpoints.only('xs')]: {
                width: 'auto',
            },
        },
        circle_chart: {
            display: 'inline-block',
            transformOrigin: '50% 50% 0px !important',
            [theme.breakpoints.only('xs')]: {
                display: 'none',
            },
        },
        circle_chart__background: {},
        circle_chart__circle: {
            transition: 'all 0.3s',
            animation: 'circle-chart-fill 0.5s reverse',
            /* 1 */
            transform: 'rotate(-90deg)',
            /* 2, 3 */
            transformOrigin: 'center',
            /* 4 */
        },
        circle_chart__info: {
            animation: 'circle-chart-appear 0.5s forwards',
            opacity: 0,
            transform: 'translateY(0.3em)',
        },
        circle_chart__percent_number: {
            fontSize: '90%',
            fontFamily: 'bariolthin',
            fontWeight: 'bold',
            fontStyle: 'normal',
            fontStretch: 'normal',
            //lineHeight: 1.14,
            letterSpacing: 'normal',
            textAlign: 'center',
            color: theme.bino.color.black,
            verticalAlign: 'middle',
            marginTop: '-10%',
        },
        circle_chart__percent: {
            fontSize: 7.5,
            fontFamily: 'bariolthin',
            fontWeight: 'normal',
            fontStyle: 'normal',
            fontStretch: 'normal',
        },
        mobile_display: {
            display: 'none',
            [`& .${classes.circle_chart__percent_number}`]: {
                fontSize: 18,
            },
            [`& .${classes.circle_chart__percent}`]: {
                fontSize: 12,
            },
            [theme.breakpoints.only('xs')]: {
                display: 'block',
            },
        },
    }));

export type CircleChartProps = {
    size?: 'small' | 'large';
    className?: string;
    /**
     * This is the value that we are presenting, it is between 0 and 100
     */
    percentage?: number;
};

export const CircleChart = (props: CircleChartProps) => {
    const { size, className, percentage, ...rest } = props;
    const { classes, cx } = useStyles();

    const hasPercentage = typeof percentage === 'number';
    const stokeDashString =
        percentage > 0 ? percentage.toString() + ',100' : '0,100';
    const textLocation = 16;

    // Calculated by using width 100px and bariolthin font then looking at offsetWidth for each number
    const numberWidths = [60, 34, 53, 54, 53, 56, 57, 48, 56, 57];

    // Calculate the percent sign position
    let textPercentLocation = null;
    if (hasPercentage) {
        const scale = 0.065; // Experimented to find this number
        const width =
            numberWidths[percentage % 10] +
            (percentage > 9
                ? numberWidths[Math.round(percentage / 10) % 10]
                : 0) +
            (percentage > 99
                ? numberWidths[Math.round(percentage / 100) % 10]
                : 0);

        textPercentLocation = textLocation + width * scale;
    }

    return (
        <div
            className={cx(className, classes.root, {
                [classes.small]: size === 'small',
                [classes.large]: size === 'large',
            })}
            {...rest}
        >
            <svg
                className={classes.circle_chart}
                viewBox="0 0 33.83098862 33.83098862"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle
                    className={classes.circle_chart__background}
                    stroke="#efefef"
                    strokeWidth="2"
                    fill="none"
                    cx="16.91549431"
                    cy="16.91549431"
                    r="15.91549431"
                />
                {hasPercentage && percentage > 0 ? (
                    <circle
                        className={classes.circle_chart__circle}
                        stroke="#ed471e"
                        strokeWidth="2"
                        strokeDasharray={stokeDashString}
                        strokeLinecap="round"
                        fill="none"
                        cx="16.91549431"
                        cy="16.91549431"
                        r="15.91549431"
                    />
                ) : (
                    ''
                )}
                <g className={classes.circle_chart__info}>
                    <text
                        className={classes.circle_chart__percent_number}
                        x={textLocation}
                        y="17"
                        alignmentBaseline="central"
                        textAnchor="middle"
                    >
                        {hasPercentage ? percentage : '?'}
                    </text>
                    {hasPercentage && (
                        <text
                            className={classes.circle_chart__percent}
                            x={textPercentLocation}
                            y="17.5"
                        >
                            %
                        </text>
                    )}
                </g>
            </svg>
            <div className={classes.mobile_display}>
                <em className={classes.circle_chart__percent_number}>
                    {hasPercentage ? percentage : '?'}
                </em>
                {hasPercentage ? (
                    <em className={classes.circle_chart__percent}>%</em>
                ) : null}
            </div>
        </div>
    );
};

export default CircleChart;
